/**
 * Agent Chat Sidebar Component
 * Dedicated sidebar for standalone agent chat interface showing chat history and workspace info
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AgentIcon } from "@/components/agents/agent-icon";
import { UserAvatar } from "@/components/ui/user-avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAgentChatHistory } from "@/hooks/use-agent-chat-history";
import { useAgent } from "@/hooks/use-agents";
import { cn } from "@/lib/utils";
import {
  useAuthActions,
  useCurrentUser,
  useCurrentWorkspace,
  useWorkspaceActions,
} from "@/store/slices";
import {
  AlertCircle,
  Archive,
  ChevronRight,
  History,
  LogOut,
  MessageSquare,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  RefreshCw,
  Search,
  Settings,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/utils/api-client";
import { useSettingsDialogsStore } from "@/store/slices/settings-dialogs-slice";
import { AgentSearchCommandDialog } from "./agent-search-command-dialog";
import { getUserData } from "@/utils/auth-storage";

/**
 * Props for the AgentChatSidebar component
 * Allows custom styling through className prop
 */
interface AgentChatSidebarProps {
  /**
   * Optional CSS class name for additional styling
   * Merged with default sidebar classes using cn utility
   */
  className?: string;
  /**
   * Whether the current user is a channel user
   * Channel users have limited access to admin features
   */
  isChannelUser?: boolean;
}

/**
 * Gets the display-friendly name for a date group key
 * Transforms internal group keys into user-readable labels
 *
 * @param {string} groupKey - The internal group key (e.g., 'today', 'yesterday')
 * @returns {string} Human-readable group name for display
 */
function getGroupDisplayName(groupKey: string): string {
  switch (groupKey) {
    case 'today':
      return 'Today'
    case 'yesterday':
      return 'Yesterday'
    case 'lastWeek':
      return 'Last 7 days'
    case 'lastMonth':
      return 'Last 30 days'
    case 'older':
      return 'Older'
    default:
      return groupKey.charAt(0).toUpperCase() + groupKey.slice(1)
  }
}

/**
 * Checks if a navigation path is currently active
 * Supports exact matches and nested route matching
 *
 * @param {string} itemUrl - The URL path to check
 * @param {string} currentPath - The current browser location path
 * @returns {boolean} True if the path is active or a parent of the current path
 */
function isPathActive(itemUrl: string, currentPath: string): boolean {
  if (itemUrl === currentPath) return true
  if (itemUrl !== "/" && currentPath.startsWith(itemUrl + "/")) return true
  return false
}

/**
 * Checks if the current page is in embed mode
 * Detects embed mode by checking for 'embed=true' URL parameter
 *
 * @returns {boolean} True if embed parameter is set to 'true'
 */
function isEmbedMode(): boolean {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('embed') === 'true'
}

/**
 * Preserves embed URL parameters when navigating between pages
 * Ensures API key and embed parameters are maintained during navigation
 *
 * @param {string} basePath - The base path to navigate to
 * @returns {string} Path with preserved embed parameters appended
 */
function getUrlWithEmbedParams(basePath: string): string {
  const urlParams = new URLSearchParams(window.location.search)
  const embedParams = new URLSearchParams()

  /**
   * Preserve embed-related parameters for consistent embed experience
   * Includes apikey, embed flag, and bubble mode flag
   */
  if (urlParams.get('apikey')) {
    embedParams.set('apikey', urlParams.get('apikey')!)
  }
  if (urlParams.get('embed')) {
    embedParams.set('embed', urlParams.get('embed')!)
  }
  if (urlParams.get('bubble')) {
    embedParams.set('bubble', urlParams.get('bubble')!)
  }

  return embedParams.toString() ? `${basePath}?${embedParams.toString()}` : basePath
}

/**
 * Sidebar component for the agent chat interface
 * Displays workspace info, chat history with infinite scroll, and user actions
 *
 * @param {AgentChatSidebarProps} props - Component props
 * @returns {JSX.Element} The rendered sidebar with all sections
 */
export function AgentChatSidebar({ className, isChannelUser = false }: AgentChatSidebarProps) {
  const { agentId, id: chatId, appId } = useParams<{ agentId: string; id?: string; appId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Check if currently on the new chat page
   * Used to highlight the New Chat button when active
   */
  const isNewChatActive = location.pathname.includes('/chat/new');
  const loadTriggerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  // Get admin user data (always called for hook rules)
  const adminUser = useCurrentUser();
  const adminWorkspace = useCurrentWorkspace();
  const { logout: adminLogout } = useAuthActions();
  const { setCurrentWorkspace } = useWorkspaceActions();

  // Get channel user data from storage (not a hook)
  const channelUserData = isChannelUser ? getUserData() : null;

  // Unified user data access - use appropriate data based on user type
  const currentUser = isChannelUser ? channelUserData : adminUser;
  const currentWorkspace = isChannelUser ? null : adminWorkspace;

  /**
   * Sidebar state and toggle function for collapse/expand
   * Used to programmatically control sidebar state
   */
  const { toggleSidebar } = useSidebar();

  /**
   * Fetch agent data for displaying in the header
   * Uses the agentId from URL params to load agent details
   */
  const { agent, loading: agentLoading } = useAgent(agentId || null);

  /**
   * Hook for managing chat history data and operations
   * Provides grouped data, loading states, and CRUD operations
   */
  const {
    chatHistory,
    groupedData,
    isLoading: historyLoading,
    isLoadingMore,
    error: historyError,
    fetchChatHistory,
    loadMore,
    hasMore,
    deleteChatFromHistory,
  } = useAgentChatHistory();

  /**
   * Ensure light mode by default on component mount
   * Removes dark class from document for consistent styling
   */
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  /**
   * Fetches workspace data for embed mode when no workspace is available
   * Uses session authentication to retrieve workspace information
   * Skipped for channel users who don't have access to admin session endpoint
   */
  useEffect(() => {
    const fetchWorkspaceForEmbed = async () => {
      try {
        const response = await api.get('/auth/session');
        if (response?.success && response?.data?.workspace) {
          setCurrentWorkspace(response.data.workspace);
        }
      } catch (error) {
      }
    };

    // Skip for channel users - they don't have access to /auth/session
    if (isEmbedMode() && !currentWorkspace && !isChannelUser) {
      fetchWorkspaceForEmbed();
    }
  }, [currentWorkspace, setCurrentWorkspace, isChannelUser]);

  /**
   * Fetches chat history when agentId changes
   * Triggers initial load of conversation history for the agent
   */
  useEffect(() => {
    if (agentId) {
      fetchChatHistory(agentId);
    }
  }, [agentId, fetchChatHistory]);

  /**
   * Tracks loading state changes for infinite scroll debouncing
   * Prevents multiple simultaneous load requests
   */
  useEffect(() => {
    isLoadingRef.current = isLoadingMore;
  }, [isLoadingMore]);

  /**
   * Sets up Intersection Observer for infinite scroll functionality
   * Automatically loads more conversations when user scrolls near bottom
   */
  useEffect(() => {
    const triggerElement = loadTriggerRef.current;
    if (!triggerElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        /**
         * Trigger load more when element is visible and conditions are met
         * Uses ref to avoid stale closure issues with state
         */
        if (entry.isIntersecting && hasMore && !isLoadingRef.current) {
          isLoadingRef.current = true;
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    if (hasMore) {
      observer.observe(triggerElement);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadMore]);

  /**
   * Navigates to new chat page for the current agent
   * Preserves embed parameters in the navigation URL
   */
  const handleNewChat = useCallback(() => {
    if (!agentId) return;
    const newChatUrl = getUrlWithEmbedParams(`/agent/${agentId}/chat/new`);
    navigate(newChatUrl);
  }, [agentId, navigate]);

  /**
   * Opens delete confirmation dialog for a chat
   * Stores chat ID for deletion confirmation
   *
   * @param {string} chatHistoryId - The ID of the chat to delete
   */
  const handleDeleteChat = useCallback((chatHistoryId: string) => {
    setChatToDelete(chatHistoryId);
    setDeleteDialogOpen(true);
  }, []);

  /**
   * Confirms and executes chat deletion
   * Navigates to new chat if currently viewing the deleted conversation
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!chatToDelete) return;

    try {
      await deleteChatFromHistory(chatToDelete);
      toast.success('Chat deleted successfully');

      /**
       * Navigate away if user is viewing the deleted chat
       * Prevents showing stale or missing conversation data
       */
      if (chatId === chatToDelete) {
        const newChatUrl = getUrlWithEmbedParams(`/agent/${agentId}/chat/new`);
        navigate(newChatUrl);
      }
      setDeleteDialogOpen(false);
      setChatToDelete(null);
    } catch (error) {
      console.error("Failed to delete chat:", error);
    }
  }, [deleteChatFromHistory, chatId, agentId, navigate, chatToDelete]);

  /**
   * Cancels the delete operation and closes the dialog
   * Resets delete-related state to initial values
   */
  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setChatToDelete(null);
  }, []);

  /**
   * Handles user logout action
   * Clears session and navigates to appropriate login page
   */
  const handleLogout = useCallback(async () => {
    try {
      if (isChannelUser) {
        // Channel user logout - clear storage and redirect to agent login
        const { clearAuthTokens } = await import('@/utils/auth-storage');
        clearAuthTokens();
        navigate(`/agent/${agentId}/login`);
      } else {
        // Admin user logout
        await adminLogout();
        navigate("/signin");
      }
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, [isChannelUser, agentId, adminLogout, navigate]);

  /**
   * Opens the workspace settings dialog over the chat. This used to open
   * `/app/${appId}/settings` in a new tab, but the standalone agent chat
   * route has no appId param — so the legacy URL was always broken there.
   * Workspace settings only needs auth (no app context), so it works on
   * both standalone and app-scoped agent chat routes.
   */
  const openWorkspaceSettings = useSettingsDialogsStore(
    (s) => s.openWorkspaceSettings,
  );
  const handleSettings = useCallback(() => {
    openWorkspaceSettings();
  }, [openWorkspaceSettings]);

  /**
   * Handles search click - opens the search command dialog
   * Shows search input with chat history and new chat option
   */
  const handleSearchClick = useCallback(() => {
    setSearchOpen(true);
  }, []);

  /**
   * Refreshes the chat history list
   * Fetches latest conversations from the server
   */
  const handleRefresh = useCallback(() => {
    if (agentId) {
      fetchChatHistory(agentId);
    }
  }, [agentId, fetchChatHistory]);

  /**
   * Toggles pin status for a chat conversation
   * Pinned chats appear at the top of the history list
   *
   * @param {string} chatId - The ID of the chat to pin/unpin
   */
  const handleTogglePin = useCallback((chatId: string) => {
    // TODO: Implement pin/unpin functionality
  }, []);

  /**
   * Archives a chat conversation
   * Archived chats are hidden from the main list but preserved
   *
   * @param {string} chatId - The ID of the chat to archive
   */
  const handleArchiveChat = useCallback((chatId: string) => {
    // TODO: Implement archive functionality
  }, []);

  /**
   * Extracts initials from a name string
   * Returns up to 2 characters for avatar fallback display
   *
   * @param {string} name - The name to extract initials from
   * @returns {string} Uppercase initials (max 2 characters)
   */
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  return (
    <Sidebar collapsible="icon" variant="default" className={cn("group-data-[side=left]:border-r-0", className)}>
      {/* Header - Agent Info with Collapse/Expand Button */}
      <SidebarHeader>
        {/* Expanded view */}
        <div className="flex items-center gap-2 px-2 py-2 group-data-[collapsible=icon]:hidden">
          {/* Agent avatar */}
          <AgentIcon
            avatar={agent?.avatar}
            name={agent?.name || "Agent"}
            size="md"
            loading={agentLoading}
          />

          {/* Agent info */}
          <div className="flex-1 min-w-0">
            {agentLoading ? (
              <div className="flex flex-col gap-1">
                <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                <div className="h-3 w-16 bg-muted rounded animate-pulse" />
              </div>
            ) : agent ? (
              <div className="flex flex-col">
                <span className="text-sm font-semibold truncate">
                  {agent.name}
                </span>
                <span className="text-xs text-muted-foreground truncate">
                  {currentWorkspace?.name || "Taqat AI"}
                </span>
              </div>
            ) : (
              <div className="flex flex-col">
                <span className="text-sm font-semibold">Agent</span>
                <span className="text-xs text-muted-foreground truncate">
                  {currentWorkspace?.name || "Taqat AI"}
                </span>
              </div>
            )}
          </div>

          {/* Collapse button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={toggleSidebar}
                className="p-1.5 rounded-lg hover:bg-accent transition-colors flex-shrink-0"
              >
                <PanelLeftClose className="h-4 w-4 text-muted-foreground" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Collapse Sidebar</TooltipContent>
          </Tooltip>
        </div>

        {/* Collapsed view - Agent icon and navigation icons */}
        <div className="hidden group-data-[collapsible=icon]:flex flex-col items-center py-2">
          {/* Agent avatar */}
          <div className="mb-2">
            <AgentIcon
              avatar={agent?.avatar}
              name={agent?.name || "Agent"}
              size="md"
              loading={agentLoading}
            />
          </div>

          {/* Navigation icons with consistent spacing */}
          <div className="flex flex-col items-center gap-1">
            {/* Expand button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleSidebar}
                  className="p-2 rounded-lg hover:bg-accent transition-colors"
                >
                  <PanelLeftOpen className="h-5 w-5 text-muted-foreground" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Expand Sidebar</TooltipContent>
            </Tooltip>

            {/* New Chat */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleNewChat}
                  className="p-2 rounded-lg hover:bg-accent transition-colors cursor-pointer"
                >
                  <MessageSquare className="h-5 w-5 text-muted-foreground" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">New Chat</TooltipContent>
            </Tooltip>

            {/* Search */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleSearchClick}
                  className="p-2 rounded-lg hover:bg-accent transition-colors"
                >
                  <Search className="h-5 w-5 text-muted-foreground" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Search Chats</TooltipContent>
            </Tooltip>

            {/* History with popup */}
            <HoverCard openDelay={200} closeDelay={300}>
              <HoverCardTrigger asChild>
                <button className="p-2 rounded-lg hover:bg-accent transition-colors">
                  <History className="h-5 w-5 text-muted-foreground" />
                </button>
              </HoverCardTrigger>
              <HoverCardContent
                side="right"
                align="start"
                className="w-80 h-[400px] p-0 overflow-hidden"
                sideOffset={8}
              >
                <div className="h-full overflow-auto p-2">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-2 py-1 mb-2">
                    Chat History
                  </div>
                  {chatHistory.length === 0 ? (
                    <div className="p-3 text-center text-sm text-muted-foreground">
                      No conversations yet
                    </div>
                  ) : (
                    chatHistory.slice(0, 10).map((chat: any) => (
                      <button
                        key={chat.id}
                        onClick={() => navigate(getUrlWithEmbedParams(`/agent/${agentId}/chat/${chat.id}`))}
                        className="flex items-start gap-2 p-2 rounded-md hover:bg-accent transition-colors w-full text-left cursor-pointer"
                      >
                        <div className="relative flex-shrink-0 mt-0.5">
                          <MessageSquare className="size-4" />
                          {chat.metadata?.status === 'streaming' && (
                            <span className="absolute -top-0.5 -right-0.5 flex size-2">
                              <span className="animate-ping absolute inline-flex size-full rounded-full bg-primary opacity-75" />
                              <span className="relative inline-flex rounded-full size-2 bg-primary" />
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {chat.metadata?.status === 'streaming' ? (
                              <span className="text-primary animate-pulse">Agent is working...</span>
                            ) : (
                              chat.title
                            )}
                          </p>
                          {chat.metadata?.status !== 'streaming' && chat.lastMessage?.preview && (
                            <p className="text-xs text-muted-foreground truncate">
                              {chat.lastMessage.preview}
                            </p>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </HoverCardContent>
            </HoverCard>

            {/* Settings - hidden for channel users */}
            {!isChannelUser && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleSettings}
                    className="p-2 rounded-lg hover:bg-accent transition-colors"
                  >
                    <Settings className="h-5 w-5 text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Settings</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </SidebarHeader>

      {/* Content */}
      <SidebarContent>
        {/* Navigation - only visible when expanded */}
        <SidebarGroup className="group-data-[collapsible=icon]:hidden">
          <SidebarGroupContent>
            <SidebarMenu>
              {/* New Chat */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={handleNewChat}
                  isActive={isNewChatActive}
                  className="data-[active=true]:bg-card data-[active=true]:dark:bg-card data-[active=true]:shadow-sm cursor-pointer"
                >
                  <MessageSquare className="size-5" />
                  <span className="flex-1">New Chat</span>
                  {isNewChatActive && (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Search */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={handleSearchClick}
                  className="cursor-pointer"
                >
                  <Search className="size-5" />
                  <span className="flex-1">Search</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Settings - hidden for channel users */}
              {!isChannelUser && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={handleSettings}
                    className="cursor-pointer"
                  >
                    <Settings className="size-5" />
                    <span className="flex-1">Settings</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Chat History - hidden when collapsed */}
        <SidebarGroup className="flex-1 min-h-0 group-data-[collapsible=icon]:hidden">
          <SidebarMenu>
            {/* Loading State - Only show for initial load when no data exists */}
            {historyLoading && chatHistory.length === 0 && (
              <SidebarMenuItem>
                <div className="p-3 text-center text-sm text-muted-foreground">
                  <RefreshCw className="size-4 animate-spin mx-auto mb-2" />
                  Loading chat history...
                </div>
              </SidebarMenuItem>
            )}

            {/* Error State */}
            {historyError && (
              <SidebarMenuItem>
                <div className="p-3 text-center text-sm text-muted-foreground">
                  <AlertCircle className="size-4 mx-auto mb-2 text-destructive" />
                  <div className="text-destructive mb-2">{historyError}</div>
                  <button
                    onClick={handleRefresh}
                    className="text-xs underline hover:no-underline"
                  >
                    Try again
                  </button>
                </div>
              </SidebarMenuItem>
            )}

            {/* Empty State */}
            {!historyError && chatHistory.length === 0 && !historyLoading && (
              <SidebarMenuItem>
                <div className="p-3 text-center text-sm text-muted-foreground">
                  No conversations yet
                </div>
              </SidebarMenuItem>
            )}

            {/* Grouped Conversations */}
            {!historyError && groupedData && (
              Object.entries(groupedData)
                .filter(([, conversations]) => conversations.length > 0)
                .map(([groupKey, groupConversations]) => (
                  <div key={groupKey}>
                    {/* Group Header */}
                    <div className="px-2 py-1 text-xs font-medium text-sidebar-foreground/60 uppercase tracking-wide">
                      {getGroupDisplayName(groupKey)}
                    </div>

                    {/* Group Conversations */}
                    {groupConversations.map((chat: any) => (
                      <SidebarMenuItem key={chat.id}>
                        <SidebarMenuButton
                          isActive={isPathActive(`/agent/${agentId}/chat/${chat.id}`, location.pathname)}
                          className="relative flex items-start gap-3 w-full min-w-0 cursor-pointer"
                          tooltip={`${chat.title} - ${chat.lastMessage?.preview || 'No messages yet'}`}
                          onClick={() => navigate(getUrlWithEmbedParams(`/agent/${agentId}/chat/${chat.id}`))}
                        >
                          {/* Chat Icon with streaming indicator */}
                          <div className="relative flex-shrink-0">
                            <MessageSquare className="size-4" />
                            {chat.metadata?.status === 'streaming' && (
                              <span className="absolute -top-0.5 -right-0.5 flex size-2">
                                <span className="animate-ping absolute inline-flex size-full rounded-full bg-primary opacity-75" />
                                <span className="relative inline-flex rounded-full size-2 bg-primary" />
                              </span>
                            )}
                          </div>

                          {/* Chat Content */}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium truncate">
                              {chat.title}
                            </h4>
                            {chat.metadata?.status === 'streaming' ? (
                              <p className="text-xs text-primary truncate animate-pulse">
                                Agent is working...
                              </p>
                            ) : chat.lastMessage?.preview ? (
                              <p className="text-xs text-muted-foreground truncate">
                                {chat.lastMessage.preview}
                              </p>
                            ) : null}
                          </div>
                        </SidebarMenuButton>

                        {/* Chat Actions */}
                        <SidebarMenuAction showOnHover>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="flex items-center justify-center">
                                <MoreHorizontal className="size-4" />
                                <span className="sr-only">Chat actions</span>
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" side="right">
                              <DropdownMenuItem
                                onClick={() => handleDeleteChat(chat.id)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="size-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </SidebarMenuAction>
                      </SidebarMenuItem>
                    ))}
                  </div>
                ))
            )}

            {/* Fallback to flat list if no grouped data */}
            {!historyError && !groupedData && chatHistory.length > 0 && (
              chatHistory.map((chat: any) => (
                <SidebarMenuItem key={chat.id}>
                  <SidebarMenuButton
                    isActive={isPathActive(`/agent/${agentId}/chat/${chat.id}`, location.pathname)}
                    className="relative flex items-start gap-3 w-full min-w-0 cursor-pointer"
                    tooltip={`${chat.title} - ${chat.lastMessage?.preview || 'No messages yet'}`}
                    onClick={() => navigate(getUrlWithEmbedParams(`/agent/${agentId}/chat/${chat.id}`))}
                  >
                    {/* Chat Icon with streaming indicator */}
                    <div className="relative flex-shrink-0">
                      <MessageSquare className="size-4" />
                      {chat.metadata?.status === 'streaming' && (
                        <span className="absolute -top-0.5 -right-0.5 flex size-2">
                          <span className="animate-ping absolute inline-flex size-full rounded-full bg-primary opacity-75" />
                          <span className="relative inline-flex rounded-full size-2 bg-primary" />
                        </span>
                      )}
                    </div>

                    {/* Chat Content */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium truncate">
                        {chat.title}
                      </h4>
                      {chat.metadata?.status === 'streaming' ? (
                        <p className="text-xs text-primary truncate animate-pulse">
                          Agent is working...
                        </p>
                      ) : chat.lastMessage?.preview ? (
                        <p className="text-xs text-muted-foreground truncate">
                          {chat.lastMessage.preview}
                        </p>
                      ) : null}
                    </div>
                  </SidebarMenuButton>

                  {/* Chat Actions */}
                  <SidebarMenuAction showOnHover>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="flex items-center justify-center">
                          <MoreHorizontal className="size-4" />
                          <span className="sr-only">Chat actions</span>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" side="right">
                        <DropdownMenuItem
                          onClick={() => handleTogglePin(chat.id)}
                        >
                          <Pin className="size-4 mr-2" />
                          Pin
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleArchiveChat(chat.id)}
                        >
                          <Archive className="size-4 mr-2" />
                          Archive
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDeleteChat(chat.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="size-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </SidebarMenuAction>
                </SidebarMenuItem>
              ))
            )}

            {/* Invisible Load Trigger for Infinite Scroll */}
            {hasMore && (
              <div
                ref={loadTriggerRef}
                className="h-1 w-full"
                style={{ minHeight: '1px' }}
              />
            )}

            {/* Loading More Indicator */}
            {isLoadingMore && (
              <SidebarMenuItem>
                <div className="p-3 text-center text-sm text-muted-foreground">
                  <RefreshCw className="size-4 animate-spin mx-auto mb-2" />
                  Loading more conversations...
                </div>
              </SidebarMenuItem>
            )}

            {/* End of List Indicator */}
            {!isLoadingMore && !hasMore && chatHistory.length > 0 && (
              <SidebarMenuItem>
                <div className="p-3 text-center text-xs text-muted-foreground border-t border-sidebar-border">
                  You've reached the end of your conversation history
                </div>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer - User profile with logout button */}
      {!isEmbedMode() && (
        <SidebarFooter>
          {/* Expanded view - User profile with logout button on right */}
          <div className="group-data-[collapsible=icon]:hidden">
            {/* Same card as `SidebarUserProfile`, which every other sidebar
                footer uses. `bg-neutral-100` was also a light-mode literal, so
                this block stayed pale grey under the dark theme. */}
            <div className="flex items-center gap-2 rounded-lg border border-sidebar-border bg-background px-2 py-2">
              <UserAvatar
                image={currentUser?.avatar}
                name={currentUser?.name}
                initials={currentUser?.name ? getInitials(currentUser.name) : "U"}
                className="size-8 flex-shrink-0"
                fallbackClassName="text-xs bg-primary text-primary-foreground"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {currentUser?.name || "User"}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {currentUser?.email || "user@example.com"}
                </p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleLogout}
                    className="flex-shrink-0 rounded-lg p-1.5 transition-colors hover:bg-sidebar-accent"
                  >
                    <LogOut className="h-4 w-4 text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Logout</TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Collapsed view - Just avatar with logout on hover */}
          <div className="hidden group-data-[collapsible=icon]:flex flex-col items-center gap-1 py-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg hover:bg-accent transition-colors"
                >
                  <LogOut className="h-5 w-5 text-muted-foreground" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Logout</TooltipContent>
            </Tooltip>
          </div>
        </SidebarFooter>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Chat</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this chat? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SidebarRail />

      {/* Search Command Dialog */}
      <AgentSearchCommandDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </Sidebar>
  );
}
